import type React from 'react';
import {
  useState, useMemo,
  useContext, createContext,
} from 'react';

interface TableSettings {
  isTopMatchVisible: boolean;
}

interface TableSettingsContextValue {
  settings: TableSettings;
  onChangeSettings: (settings: Partial<TableSettings>) => void;
}

const tableSettingsContext = createContext<TableSettingsContextValue | undefined>(
  undefined,
);

interface TableSettingsContextProviderProps {
  children: React.ReactNode;
}

function TableSettingsContextProvider({
  children,
}: TableSettingsContextProviderProps): React.JSX.Element {
  const [settings, setSettings] = useState<TableSettings>({
    isTopMatchVisible: true,
  });

  const value = useMemo(
    () => ({
      settings,
      onChangeSettings: (newSettings: Partial<TableSettings>) => {
        setSettings((prev) => ({
          ...prev,
          ...newSettings,
        }));
      },
    }),
    [settings],
  );

  return (
    <tableSettingsContext.Provider value={value}>
      {children}
    </tableSettingsContext.Provider>
  );
}

function useTableSettingsContext(): TableSettingsContextValue {
  const context = useContext(tableSettingsContext);
  if (!context) throw new Error('useTableSettingsContext must be used within TableSettingsContextProvider');
  return context;
}

export {
  TableSettingsContextProvider,
  useTableSettingsContext,
};
